import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  DataGridPro,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridActionsCellItem,
  GridToolbarContainer,
} from '@mui/x-data-grid-pro';
import './DataGrid.scss';
import styles from './DataGrid.module.css';
import {
  Button,
  Icon,
  TextInput,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Modal,
  ModalRoot,
  ModalHeader,
  ModalContent,
  ModalFooter,
} from '@pella/sash-framework';

import InputForm from './InputForm';
import EditComponent from './EditComponent';
import { showAlert } from '../../../utils/alerts';
import Help from '../../molecules/Help';

/**
 * Escapes special regex characters in a string for safe use in a RegExp.
 * @param {string} value - The string to escape.
 * @returns {string} The escaped string.
 */
const escapeRegExp = (value) => value.replaceAll(/[-[\]{}()*+?.,\\^$|#\s]/gu, '\\$&');

/**
 * Returns whether a column should be included in the DataGrid display.
 * @param {object} c - The column definition object.
 * @returns {boolean} True if the column should be shown.
 */
const filterFields = (c) => {
  if (c.hideColumn) {
    return false;
  }
  return c.hide !== true;
};

const CustomDetailPanelToggle = (props) => {
  const { value: isExpanded } = props;

  return (
    <Button
      size='sm'
      color='secondary'
      iconLeft={isExpanded ? 'expand_less' : 'expand_more'}
      aria-label={isExpanded ? 'Close' : 'Open'}
    />
  );
};

const QuickSearchToolbar = (props) => (
  <div className={styles.quickSearchToolbar}>
    <TextInput
      id='datagrid-quick-search'
      labelText=''
      value={props.value}
      onChange={props.onChange}
      placeholder='Search…'
    />
    <Button
      iconLeft='clear'
      aria-label='Clear'
      size='sm'
      color='secondary'
      className={props.value ? styles.visible : styles.hidden}
      onClick={props.clearSearch}
    />
  </div>
);

const EditToolbarComponent = ({
  title: toolbarTitle,
  editable: toolbarEditable,
  extraOption: toolbarExtraOption,
  ...props
}) => (
  <>
    <GridToolbarContainer className='httpCallTitle'>{toolbarTitle}</GridToolbarContainer>
    <GridToolbarContainer className='httpCallActionDiv'>
      {toolbarEditable?.addNewRow && (
        <Button color='secondary' size='sm' onClick={props.newRecord} iconLeft='add'>
          Add
        </Button>
      )}
      {toolbarExtraOption
        ?.filter((e) => e.isFreeAction === true)
        ?.map((e) => (
          <Button
            key={`${e.tooltip}-${e.icon}`}
            color='secondary'
            size='sm'
            onClick={(c) => e.onClick(c, props)}
            id={`extra-option-${e.tooltip}`}
            iconLeft={e.icon}
          >
            {e.tooltip}
          </Button>
        ))}
      {props?.quickFilter && <QuickSearchToolbar {...props} />}
    </GridToolbarContainer>
  </>
);

// Column order and visibility changes are intentionally not persisted in this implementation
const handleOrderChange = () => {
  /* no-op */
};
const handleColumnVisibility = () => {
  /* no-op */
};

const AnyTooltipTrigger = /** @type {import('react').ElementType} */ (TooltipTrigger);

/**
 * Full-featured DataGrid panel with editing, filtering, and optional nested tables.
 * @param {object} props - Component props
 * @param {boolean} [props.checkboxSelection] - Enable row selection checkboxes
 * @param {boolean} [props.checkResponse] - Whether to validate the response status after add/update
 * @param {Array<object>} props.columns - Column definitions
 * @param {boolean} [props.copy] - Enable row copy action
 * @param {string} [props.density] - Row density setting
 * @param {object} [props.detailPanel] - Detail panel configuration
 * @param {boolean} [props.dynamicHeight] - Use dynamic height for grid
 * @param {object} [props.editable] - Edit configuration
 * @param {Array<object>} [props.extraOption] - Extra action options
 * @param {(params: object) => string} [props.getRowClassName] - Callback to get row class name
 * @param {object} [props.helper] - Helper utilities
 * @param {(selectionModel: unknown) => void} [props.onSelectionModelChange] - Callback for selection changes
 * @param {number} [props.pageSize] - Default page size
 * @param {string|number} [props.parentId] - Parent ID for records
 * @param {boolean} [props.quickFilter] - Enable quick search filter
 * @param {object} [props.remember] - Remember settings configuration
 * @param {() => void} [props.resetValues] - Callback to reset values
 * @param {Array<object>} props.rows - Row data
 * @param {Array<string|number>} [props.selectionModel] - Selected row IDs
 * @param {boolean} [props.skipDeleteConfirm] - Skip delete confirmation
 * @param {string} [props.title] - Grid title
 * @param {string} [props.validateDelete] - Field to validate on delete
 */
const DataGridPanel = ({
  checkboxSelection,
  checkResponse,
  columns: propsColumns,
  copy,
  density,
  detailPanel,
  dynamicHeight: propsDynamicHeight,
  editable,
  extraOption,
  getRowClassName,
  helper,
  onSelectionModelChange,
  pageSize: propsPageSize,
  parentId,
  quickFilter,
  remember,
  resetValues,
  rows: propsRows,
  selectionModel,
  skipDeleteConfirm,
  title,
  validateDelete,
}) => {
  const [pinned, setPinned] = useState(() => {
    if (remember) {
      const pins = window.sessionStorage.getItem(`pinned_${remember.table}`);
      if (pins) return JSON.parse(pins);
    }
    return { left: [], right: [] };
  });
  const [pageSize, setPageSize] = useState(propsPageSize || 20);
  const [errors, setErrors] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [isCopy, setIsCopy] = useState(false);
  const [open, setOpen] = useState(false);
  const [inputValues, setInputValues] = useState({});
  const [disableNested, setDisableNested] = useState(false);
  const [oldTableData, setOldTableData] = useState({});
  const [tableConfig, setTableConfig] = useState(() => {
    if (remember) {
      const config = window.sessionStorage.getItem(remember.table || '');
      if (typeof config === 'string') return JSON.parse(config);
    }
    return {};
  });
  const [searchText, setSearchText] = useState('');
  const [dynamicHeight, setDynamicHeight] = useState(window.innerHeight - 160);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [deleteInputValue, setDeleteInputValue] = useState('');

  useEffect(() => {
    const handleResize = () => {
      setDynamicHeight(window.innerHeight - 160);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const rows = useMemo(() => {
    if (!searchText) return propsRows;
    const searchRegex = new RegExp(escapeRegExp(searchText), 'iu');
    return propsRows.filter((row) => Object.keys(row).some((field) => searchRegex.test(row[field]?.toString())));
  }, [propsRows, searchText]);

  const getDetailPanelContent = useCallback(
    ({ row }) => <div className={styles.detailPanelContent}>{`Order #${row.id}`}</div>,
    [],
  );

  const toggleDialog = (disNested = false) => {
    if (open) {
      setTimeout(() => {
        setErrors({});
        setInputValues({});
        setDisableNested(disNested);
      }, 500);
    } else {
      setErrors({});
      setInputValues({});
      setDisableNested(disNested);
    }
    setIsCopy(false);
    setOpen(!open);
  };

  const editRecord = (id) => {
    const record = propsRows.find((r) => r.id === id);
    toggleDialog();
    setInputValues(record);
    setIsEdit(true);
    setOldTableData({ ...record });
  };

  const newRecord = () => {
    setIsEdit(false);
    toggleDialog(true);
  };

  const getRowData = (id, callBack) => {
    const record = propsRows.find((r) => r.id === id);
    callBack(record);
  };

  const deleteRecord = (id) => {
    setPendingDeleteId(id);
    setDeleteInputValue('');
  };

  const handleDeleteConfirm = () => {
    const id = pendingDeleteId;
    setPendingDeleteId(null);
    const record = propsRows.find((r) => r.id === id);
    if (parentId) {
      record.parentId = parentId;
    }
    if (validateDelete) {
      if (deleteInputValue === record[validateDelete]) {
        editable
          ?.onRowDelete(record)
          .then(() => {
            if (!skipDeleteConfirm) showAlert({ title: 'Done!', text: '', type: 'success' });
            if (resetValues) resetValues();
            return undefined;
          })
          .catch(() => {
            showAlert({ title: 'Failed to delete.', text: '', type: 'error' });
          });
      } else {
        showAlert({ title: 'Failed to delete.', text: '', type: 'error' });
      }
    } else {
      editable
        ?.onRowDelete(record)
        .then(() => {
          if (!skipDeleteConfirm) showAlert({ title: 'Done!', text: '', type: 'success' });
          if (resetValues) resetValues();
          return undefined;
        })
        .catch(() => {
          if (!skipDeleteConfirm) showAlert({ title: 'Failed to delete.', text: '', type: 'error' });
        });
    }
  };

  const copyRow = (id) => {
    toggleDialog();
    let record = propsRows.find((r) => r.id === id);
    record = { ...record };
    setIsEdit(false);
    setIsCopy(true);
    setInputValues(record);
  };

  const getActionIcons = (id) => {
    const actions = [];
    if (editable?.onRowUpdateModal) {
      actions.push(
        <GridActionsCellItem
          icon={<Icon name='edit' />}
          label='Edit'
          className='textPrimary'
          onClick={() => {
            editRecord(id);
          }}
          color='inherit'
        />,
      );
    }

    if (copy) {
      actions.push(
        <GridActionsCellItem
          icon={<Icon name='content_copy' />}
          label='Copy'
          className='textPrimary'
          onClick={() => {
            copyRow(id);
          }}
          color='inherit'
        />,
      );
    }

    const extraOptions = extraOption?.filter((e) => e.isFreeAction !== true) ?? [];
    for (const e of extraOptions) {
      actions.push(
        <GridActionsCellItem
          icon={<Icon name={e.icon} />}
          label={e.tooltip}
          onClick={() => getRowData(id, e.onClick)}
        />,
      );
    }

    if (editable?.onRowDelete) {
      actions.push(
        <GridActionsCellItem
          icon={<Icon name='delete' />}
          label='Delete'
          onClick={() => deleteRecord(id)}
          color='inherit'
        />,
      );
    }

    return actions;
  };

  const columns = useMemo(() => {
    const cols = [];

    if (detailPanel) {
      cols.push({
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        renderCell: (params) => <CustomDetailPanelToggle {...params} />,
      });
    }

    const actionIcons = getActionIcons(1);

    const headerName = helper ? <Help item={helper?.item} access={helper?.access ?? {}} tip={helper?.tip} /> : '';

    if (actionIcons.length) {
      cols.push({
        headerName,
        field: 'actions',
        type: 'actions',
        cellClassName: 'actions',
        width: 40 * actionIcons.length,
        getActions: ({ id }) => getActionIcons(id),
      });
    }
    const existingColumns = propsColumns
      .filter((c) => filterFields(c))
      .map((col) => {
        const c = { ...col };
        if (!c.renderCell && c.valueOptions) {
          c.renderCell = (params) => {
            const valueOptions = typeof c.valueOptions === 'function' ? c.valueOptions(propsRows) : c.valueOptions;

            const option = valueOptions?.find((rl) => {
              if (typeof rl === 'object') {
                return rl.value?.toString() === params.row[c.field]?.toString();
              }
              return rl?.toString() === params.row[c.field]?.toString();
            });
            const value = option?.label || option || '';
            return (
              <Tooltip>
                <AnyTooltipTrigger asChild>
                  <span>{value}</span>
                </AnyTooltipTrigger>
                <TooltipContent>{value}</TooltipContent>
              </Tooltip>
            );
          };
        }
        if (c.editable) {
          c.renderEditCell = (params) => (
            <EditComponent {...params} fullFiled={c} parentId={parentId} resetValues={resetValues} />
          );
        }
        return {
          ...tableConfig[c.field],
          ...c,
        };
      });

    return [...cols, ...existingColumns];
    // getActionIcons references editable, copy, extraOption (props) and editRecord/copyRow/getRowData/deleteRecord (internal fns).
    // We intentionally depend on propsColumns and tableConfig as the primary triggers, matching the original effect's deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propsColumns, tableConfig, detailPanel, helper, parentId, resetValues, editable, copy, extraOption, propsRows]);

  const addUpdateRecord = () => {
    if (editable?.addNewRow || editable?.onRowUpdateModal) {
      const f = isEdit ? editable.onRowUpdateModal : editable.addNewRow;
      const submitValues = parentId ? { ...inputValues, parentId } : inputValues;
      f(submitValues, oldTableData)
        .then((result) => {
          setErrors({});
          toggleDialog();
          if (checkResponse) {
            if (result?.status?.toString() === '201' || result?.status?.toString() === '200') {
              showAlert({ title: 'Done!', text: '', type: 'success' });
            } else {
              showAlert({ title: result?.message, text: '', type: 'error' });
            }
          } else {
            showAlert({ title: 'Done!', text: '', type: 'success' });
          }
          if (resetValues) resetValues();
          return result;
        })
        .catch((error) => {
          if (typeof error !== 'string') {
            const errorFields = {};
            for (const e of error?.response?.data?.errors ?? []) {
              errorFields[e.param] = e.msg;
            }
            setErrors(errorFields);
          } else {
            showAlert({ title: error || 'Something went wrong', text: '', type: 'warning' });
          }
        });
    }
  };

  const setConfigValue = (column, setting, value) => {
    const config = { ...tableConfig };
    config[column] = config[column] || {};
    config[column][setting] = value;

    setTableConfig({ ...config });
    window.sessionStorage.setItem(remember.table, JSON.stringify(config));
  };

  const handleColumnWidthChange = ({ colDef }) => {
    if (remember) {
      setConfigValue(colDef.field, 'width', colDef.computedWidth);
    }
  };

  const handlePinnedColumns = (data) => {
    window.sessionStorage.setItem(`pinned_${remember.table}`, JSON.stringify(data));
    setPinned(data);
  };

  return (
    <div className='delinquent-orders dataGrid'>
      <div className={styles.fullWidth}>
        <DataGridPro
          className='data-grid-pro-margin-0'
          sx={{ m: 2 }}
          density={density || 'compact'}
          rows={rows}
          columns={columns}
          rowThreshold={0}
          getDetailPanelContent={detailPanel ? getDetailPanelContent : undefined}
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[5, 10, 20, 50, 100]}
          pagination={!propsDynamicHeight}
          style={{ height: propsDynamicHeight ? dynamicHeight : '' }}
          autoHeight={!propsDynamicHeight}
          components={{ Toolbar: EditToolbarComponent }}
          componentsProps={{
            toolbar: {
              title,
              value: searchText,
              onChange: (event) => setSearchText(event.target.value),
              clearSearch: () => setSearchText(''),
              quickFilter,
              editable,
              extraOption,
              newRecord,
              parentId,
              resetValues,
            },
          }}
          onColumnWidthChange={handleColumnWidthChange}
          onPinnedColumnsChange={handlePinnedColumns}
          pinnedColumns={pinned}
          onColumnOrderChange={handleOrderChange}
          onColumnVisibilityModelChange={handleColumnVisibility}
          getRowClassName={getRowClassName}
          checkboxSelection={checkboxSelection}
          onSelectionModelChange={onSelectionModelChange}
          selectionModel={selectionModel}
        />
      </div>
      <InputForm
        open={open}
        isEdit={isEdit}
        isCopy={isCopy}
        title={title}
        toggleDialog={toggleDialog}
        addUpdateRecord={addUpdateRecord}
        setInputValues={setInputValues}
        inputValues={inputValues}
        oldTableData={oldTableData}
        columns={propsColumns}
        errors={errors}
        disableNested={disableNested}
        helper={helper}
      />
      <ModalRoot
        open={pendingDeleteId !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setPendingDeleteId(null);
        }}
      >
        <Modal showClose={false}>
          <ModalHeader>Are you sure?</ModalHeader>
          <ModalContent>
            <p>{validateDelete ? 'Confirm deletion by typing the name' : "You won't be able to revert this!"}</p>
            {validateDelete && (
              <TextInput
                id='delete-confirm-input'
                labelText='Confirm name'
                value={deleteInputValue}
                onChange={(e) => setDeleteInputValue(e.target.value)}
              />
            )}
          </ModalContent>
          <ModalFooter>
            <Button size='sm' color='secondary' onClick={() => setPendingDeleteId(null)}>
              Cancel
            </Button>
            <Button size='sm' color='primary' onClick={handleDeleteConfirm}>
              {validateDelete ? 'Confirm' : 'Yes, delete it!'}
            </Button>
          </ModalFooter>
        </Modal>
      </ModalRoot>
    </div>
  );
};
export default DataGridPanel;
